//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import SunEditor from 'suneditor-react';
import {
  blockquote,
  font,
  fontColor,
  fontSize,
  hiliteColor,
  image,
  lineHeight,
  link,
  list,
  paragraphStyle,
  template,
  textStyle,
} from "suneditor/src/plugins";
import { SPANISH_ITEM_LOWER, SPANISH_ITEM_UPPER } from "utils/config/reference";
import { useFocusOutside } from "./OnClickOutside";
import * as Sentry from '@sentry/react';
import { useCurrentUser } from "store/users/selectors";
import { useAlert } from "./UI/alertSakeBar/alertProvider";
import {
  getHistoryEntryContent,
  syncEditorAfterHistoryApply,
  syncEditorPlaceholder,
} from "utils/studentEditorHistoryUtils";
//  -- Constants ----------------------



const STUDENT_UNDO_ICON =
  '<svg class="se-svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/></svg>';

const STUDENT_REDO_ICON =
  '<svg class="se-svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.06-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z"/></svg>';

const BASE_BUTTON_LIST = [
  ['font', 'fontSize'],
  ['bold', 'underline', 'italic'],
  ['removeFormat'],
  ['fontColor', 'hiliteColor'],
  ['list'],
  [
    {
      name: 'SPANISH_ITEM_LOWER',
      dataCommand: 'SPANISH_ITEM_LOWER',
      buttonClass: '',
      title: `${SPANISH_ITEM_LOWER[0]}`,
      dataDisplay: 'submenu',
      innerHTML: `<span>${SPANISH_ITEM_LOWER[0]}</span>`,
    },

    //...
  ],
  [
    {
      name: 'SPANISH_ITEM_UPPER',
      dataCommand: 'SPANISH_ITEM_UPPER',
      buttonClass: '',
      title: `${SPANISH_ITEM_UPPER[0]}`,
      dataDisplay: 'submenu',
      innerHTML: `<span>${SPANISH_ITEM_UPPER[0]}</span>`,
    }
  ]
]
const BASE_BUTTON_LIST_LIMITED = [
  ['bold', 'underline', 'italic'],
  [
    {
      name: 'SPANISH_ITEM_LOWER',
      dataCommand: 'SPANISH_ITEM_LOWER',
      buttonClass: '',
      title: `${SPANISH_ITEM_LOWER[0]}`,
      dataDisplay: 'submenu',
      innerHTML: `<span>${SPANISH_ITEM_LOWER[0]}</span>`,
    },

    //...
  ],
  [
    {
      name: 'SPANISH_ITEM_UPPER',
      dataCommand: 'SPANISH_ITEM_UPPER',
      buttonClass: '',
      title: `${SPANISH_ITEM_UPPER[0]}`,
      dataDisplay: 'submenu',
      innerHTML: `<span>${SPANISH_ITEM_UPPER[0]}</span>`,
    }
  ]
]
const ACCENT_ROW_SIZE = 4;

const getAccentGridSubmenuHtml = (items, buttonClassName) => {
  const rows = [];
  for (let index = 0; index < items.length; index += ACCENT_ROW_SIZE) {
    const rowItems = items.slice(index, index + ACCENT_ROW_SIZE);
    rows.push(`<div style="display:grid;grid-template-columns:repeat(${rowItems.length}, minmax(28px, auto));gap:6px;">${rowItems
      .map(
        (item) =>
          `<button type="button" class="${buttonClassName}" value="${item}" style="min-width:28px;height:28px;border:1px solid #d9d9d9;border-radius:6px;background:#fff;cursor:pointer;">${item}</button>`
      )
      .join("")}</div>`);
  }

  return `<div class="se-list-inner se-list-font-size" style="width:auto;min-width:126px;"><div style="display:flex;flex-direction:column;gap:6px;padding:4px;">${rows.join("")}</div></div>`;
};

const SPANISH_ITEM_LOWER_PLUGIN = (editorRef) => (
  {
    name: 'SPANISH_ITEM_LOWER',
      display: 'submenu',
        add: function (core, targetElement) {
          let listDiv = this.setSubmenu.call(core);
          var self = this;
          listDiv.querySelectorAll('.se-btn-list-lower').forEach(function (btn) {
            btn.addEventListener('click', self.onClick.bind(core));
          });
          core.initMenuTarget(this.name, targetElement, listDiv);
        },

    setSubmenu: function () {
      const listDiv = this.util.createElement('DIV');
      listDiv.className = 'se-submenu se-list-layer';
      listDiv.innerHTML = getAccentGridSubmenuHtml(SPANISH_ITEM_LOWER, 'se-btn-list-lower');

      return listDiv;
    },
    // onClick: function (e) {
    //   const value = e.target.value;
    //   const node = this.util.createElement('span');
    //   this.util.addClass(node, 'se-custom-tag');
    //   node.textContent = value;
    //   this.insertNode(node);

    //   const zeroWidthSpace = this.util.createTextNode(this.util.zeroWidthSpace);
    //   node.parentNode.insertBefore(zeroWidthSpace, node.nextSibling);

    //   this.submenuOff();

    //   // ✅ Force onChange manually
    //   if (editorRef.current && typeof editorRef.current.getContents === 'function') {
    //     const content = editorRef.current.getContents(true);
    //     if (typeof editorRef.current.onChange === 'function') {
    //       editorRef.current.onChange(content);
    //     }
    //   }
    // }
    onClick: function (e) {

      const core = this;                // because onClick is bound with .bind(core)

      if (!core.context || !core.context.element || !core.context.element.wysiwyg) {


        console.warn('SunEditor context not properly initialized');
        return;
      }
    
      const value = e.target.value;
     
      const node = core.util.createElement('span');
    
      core.util.addClass(node, 'se-custom-tag');
    
      node.textContent = value;
     
      core.insertNode(node);
     
      // Move caret just after the inserted node (optional but nice UX)
    
      try { core.setRange(node, 1); } catch (_) {}
     
      // Close submenu
    
      core.submenuOff();
     
      // Ensure the editor is focused
    
      core.focus();
     
      // 🔥 Tell SunEditor “content changed” so it hides the placeholder
    
      // Dispatch a native 'input' event on the contentEditable surface
    
      const wysiwyg = core.context.element.wysiwyg;
    
      if (wysiwyg) {
    
        const ev = new Event('input', { bubbles: true });
    
        wysiwyg.dispatchEvent(ev);
    
      }
     
      // (Optional) Also trigger 'keyup' if your build needs it:
    
      // wysiwyg.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true }));
     
      // If you still want to call your external onChange:
    
      if (editorRef.current && typeof editorRef.current.getContents === 'function') {
        const content = editorRef.current.getContents(true);
        if (typeof editorRef.current.onChange === 'function') {
          editorRef.current.onChange(content);
        }
      }
    }

  }
);
const SPANISH_ITEM_UPPER_PLUGIN = (editorRef) => (
  {
    name: 'SPANISH_ITEM_UPPER',
      display: 'submenu',
        add: function (core, targetElement) {
          let listDiv = this.setSubmenu.call(core);
          var self = this;
          listDiv.querySelectorAll('.se-btn-list-upper').forEach(function (btn) {
            btn.addEventListener('click', self.onClick.bind(core));
          });
          core.initMenuTarget(this.name, targetElement, listDiv);
        },

    setSubmenu: function () {
      const listDiv = this.util.createElement('DIV');
      listDiv.className = 'se-submenu se-list-layer';
      listDiv.innerHTML = getAccentGridSubmenuHtml(SPANISH_ITEM_UPPER, 'se-btn-list-upper');

      return listDiv;
    },
    // onClick: function (e) {
    //   const value = e.target.value;
    //   const node = this.util.createElement('span');
    //   this.util.addClass(node, 'se-custom-tag');
    //   node.textContent = value;
    //   this.insertNode(node);

    //   const zeroWidthSpace = this.util.createTextNode(this.util.zeroWidthSpace);
    //   node.parentNode.insertBefore(zeroWidthSpace, node.nextSibling);

    //   this.submenuOff();

    //   if (editorRef.current && typeof editorRef.current.getContents === 'function') {
    //     const content = editorRef.current.getContents(true);
    //     if (typeof editorRef.current.onChange === 'function') {
    //       editorRef.current.onChange(content);
    //     }
    //   }
    // }
    onClick: function (e) {

      const core = this;                // because onClick is bound with .bind(core)

      if (!core.context || !core.context.element || !core.context.element.wysiwyg) {
        console.warn('SunEditor context not properly initialized');
        return;
      }
    
      const value = e.target.value;
     
      const node = core.util.createElement('span');
    
      core.util.addClass(node, 'se-custom-tag');
    
      node.textContent = value;
     
      core.insertNode(node);
     
      // Move caret just after the inserted node (optional but nice UX)
    
      try { core.setRange(node, 1); } catch (_) {}
     
      // Close submenu
    
      core.submenuOff();
     
      // Ensure the editor is focused
    
      core.focus();
     
      // 🔥 Tell SunEditor “content changed” so it hides the placeholder
    
      // Dispatch a native 'input' event on the contentEditable surface
    
      const wysiwyg = core.context.element.wysiwyg;
    
      if (wysiwyg) {
    
        const ev = new Event('input', { bubbles: true });
    
        wysiwyg.dispatchEvent(ev);
    
      }
     
      // (Optional) Also trigger 'keyup' if your build needs it:
    
      // wysiwyg.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true }));
     
      // If you still want to call your external onChange:
    
      if (editorRef.current && typeof editorRef.current.getContents === 'function') {
        const content = editorRef.current.getContents(true);
        if (typeof editorRef.current.onChange === 'function') {
          editorRef.current.onChange(content);
        }
      }
    }
  }
);

//  ----------------------------------------------------------------------------
//  Styles
//  ----------------------------------------------------------------------------

const Container = styled.div`
  width: 100%;

  .sun-editor,
  .sun-editor .se-wrapper,
  .sun-editor .se-container,
  .sun-editor .se-toolbar-container,
  .sun-editor .se-toolbar {
    overflow: visible !important;
  }

  .sun-editor,
  .sun-editor .se-wrapper,
  .sun-editor .se-container {
    position: relative;
    z-index: 1;
  }

  .sun-editor .se-wrapper-wysiwyg {
    position: relative;
    z-index: 1;
  }

  .sun-editor .se-wrapper .se-placeholder {
    z-index: 2;
  }

  .sun-editor .se-toolbar,
  .sun-editor .se-toolbar-container {
    position: relative;
    z-index: 20;
  }

  .sun-editor .se-submenu,
  .sun-editor .se-list-layer {
    position: absolute !important;
    z-index: 10000 !important;
  }
`;

//  ----------------------------------------------------------------------------
//  Helpers
//  ----------------------------------------------------------------------------
//  ----------------------------------------------------------------------------
//  Container
//  ----------------------------------------------------------------------------
//
//  Not found container
//
//  -- Description
//  Failed route catch container
//
//  -- Props
//  ...
//
//  -- State
//  ...
//
//  -- Methods
//  ...
//
//  -- Usage
//  ...
//

// Safari detection function
const isSafariIOS = () => {
  return /iP(hone|ad)/.test(navigator.platform) && /Safari/.test(navigator.userAgent);
};

const TextEditor = ({ handleChange, value, autoFocus = true, disabled, containerClassName="", disabledImage=true , has_limited_option=false , containerStyle={}, from="",isOpen = false, placeholder="Student answer...", disableWordSuggestions = false}) => {
  const showAlert = useAlert();
  const editorRef = useRef();
  const user = useCurrentUser();
  const isPasteRestrictedUser =
    user?.role !== "owe_admin" &&
    user?.role !== "teacher" &&
    !window.location.origin.includes("localhost");
  const { ref, isFocused } = useFocusOutside();
  const studentHistoryRef = useRef({ stack: [], index: 0 });
  const studentHistoryDelayRef = useRef(null);
  const isApplyingStudentHistoryRef = useRef(false);
  const studentUndoHandlerRef = useRef(() => {});
  const studentRedoHandlerRef = useRef(() => {});
  const [lastContent, setLastContent] = useState(value);
  const pasteBlockRef = useRef({
    lastPasteTime: 0,
    pasteCount: 0,
    maxPasteAttempts: 3,
    resetTimeout: 1000,
  });
  const initialRenderRef = useRef(true);

  const showPasteBlockedAlert = useCallback(() => {
    try {
      if (user?.role === "owe_admin" || user?.role === "teacher" || window.location.origin.includes("localhost")) {
        return;
      }
      const now = Date.now();
      const pasteBlock = pasteBlockRef.current;

      if (now - pasteBlock.lastPasteTime < pasteBlock.resetTimeout) {
        pasteBlock.pasteCount += 1;
      } else {
        pasteBlock.pasteCount = 1;
      }
      pasteBlock.lastPasteTime = now;
      showAlert("Paste not allowed", "error");

      if (pasteBlock.pasteCount > pasteBlock.maxPasteAttempts) {
        showAlert("Paste not allowed", "error");
      }
    } catch (error) {
      Sentry.captureException(`253 Error in showPasteBlockedAlert: ${error}`);
    }
  }, [showAlert, user]);

  const pushStudentHistory = useCallback((content) => {
    if (isApplyingStudentHistoryRef.current) return;
    const history = studentHistoryRef.current;
    if (getHistoryEntryContent(history.stack[history.index]) === content) return;

    history.stack = history.stack.slice(0, history.index + 1);
    history.stack.push(content);
    history.index = history.stack.length - 1;

    if (history.stack.length > 100) {
      history.stack.shift();
      history.index -= 1;
    }
  }, []);

  const schedulePushStudentHistory = useCallback(
    (content) => {
      if (studentHistoryDelayRef.current) {
        clearTimeout(studentHistoryDelayRef.current);
      }
      studentHistoryDelayRef.current = setTimeout(() => {
        studentHistoryDelayRef.current = null;
        pushStudentHistory(content);
      }, 400);
    },
    [pushStudentHistory]
  );

  const applyStudentHistoryContent = useCallback(
    (historyItem) => {
      const editor = editorRef.current;
      if (!editor?.core) return;

      const content = getHistoryEntryContent(historyItem);

      isApplyingStudentHistoryRef.current = true;
      const { core } = editor;
      const convertValue =
        content == null ? "" : core.convertContentsForEditor(content, null, null);
      core.context.element.wysiwyg.innerHTML = convertValue;
      core._resetComponents();
      syncEditorAfterHistoryApply(core, content);

      const cleanValue =
        typeof content === "string" ? content.replace(/<img[^>]*>/g, "") : content;
      setLastContent(cleanValue);
      handleChange(cleanValue);
      isApplyingStudentHistoryRef.current = false;
    },
    [handleChange]
  );

  const studentUndo = useCallback(() => {
    const history = studentHistoryRef.current;
    if (history.index <= 0) return;
    history.index -= 1;
    applyStudentHistoryContent(history.stack[history.index]);
  }, [applyStudentHistoryContent]);

  const studentRedo = useCallback(() => {
    const history = studentHistoryRef.current;
    if (history.index >= history.stack.length - 1) return;
    history.index += 1;
    applyStudentHistoryContent(history.stack[history.index]);
  }, [applyStudentHistoryContent]);

  studentUndoHandlerRef.current = studentUndo;
  studentRedoHandlerRef.current = studentRedo;

  const studentUndoPlugin = useMemo(
    () => ({
      name: "studentUndo",
      display: "command",
      title: "Undo",
      innerHTML: STUDENT_UNDO_ICON,
      add: function (core, targetElement) {
        core.context.studentUndo = { targetButton: targetElement };
      },
      action: function studentUndoAction() {
        studentUndoHandlerRef.current();
      },
    }),
    []
  );

  const studentRedoPlugin = useMemo(
    () => ({
      name: "studentRedo",
      display: "command",
      title: "Redo",
      innerHTML: STUDENT_REDO_ICON,
      add: function (core, targetElement) {
        core.context.studentRedo = { targetButton: targetElement };
      },
      action: function studentRedoAction() {
        studentRedoHandlerRef.current();
      },
    }),
    []
  );

  const editorButtonList = useMemo(
    () => [
      ...(has_limited_option ? BASE_BUTTON_LIST_LIMITED : BASE_BUTTON_LIST),
      isPasteRestrictedUser ? ["studentUndo", "studentRedo"] : ["undo", "redo"],
    ],
    [has_limited_option, isPasteRestrictedUser]
  );

  const editorPlugins = useMemo(() => {
    const plugins = [
      blockquote,
      font,
      fontColor,
      fontSize,
      hiliteColor,
      lineHeight,
      list,
      paragraphStyle,
      template,
      textStyle,
      link,
      SPANISH_ITEM_LOWER_PLUGIN(editorRef),
      SPANISH_ITEM_UPPER_PLUGIN(editorRef),
      ...(isPasteRestrictedUser ? [studentUndoPlugin, studentRedoPlugin] : []),
    ];

    if (!disabledImage) {
      plugins.push(image);
    }

    return plugins;
  }, [disabledImage, isPasteRestrictedUser, studentRedoPlugin, studentUndoPlugin]);

  const getSunEditorInstance = useCallback(
    (sunEditor) => {
      editorRef.current = sunEditor;
      if (disableWordSuggestions) {
        const wysiwyg = sunEditor?.context?.element?.wysiwyg;
        if (wysiwyg) {
          wysiwyg.setAttribute("spellcheck", "false");
          wysiwyg.spellcheck = false;
          wysiwyg.setAttribute("autocorrect", "off");
          wysiwyg.setAttribute("autocorrect", "false");
          wysiwyg.setAttribute("autocapitalize", "none");
          wysiwyg.setAttribute("autocomplete", "off");
          wysiwyg.setAttribute("aria-autocomplete", "none");
          wysiwyg.setAttribute("x-webkit-speech", "off");
          wysiwyg.setAttribute("x-webkit-voice", "off");
          wysiwyg.setAttribute("data-gramm", "false");
          wysiwyg.setAttribute("data-gramm_editor", "false");
          wysiwyg.setAttribute("data-grammarly", "false");
          wysiwyg.setAttribute("data-grammarly_editor", "false");
          wysiwyg.setAttribute("data-enable-grammarly", "false");
        }
      }
      if (isPasteRestrictedUser) {
        const initial = sunEditor.getContents(true) || value || "";
        studentHistoryRef.current = { stack: [initial], index: 0 };
      }
      syncEditorPlaceholder(sunEditor.core, sunEditor.getContents(true) || value || "");
    },
    [disableWordSuggestions, isPasteRestrictedUser, value]
  );

  useEffect(() => {
    return () => {
      if (studentHistoryDelayRef.current) {
        clearTimeout(studentHistoryDelayRef.current);
      }
    };
  }, []);

  useEffect(() => {
    
    if(from === "worksheet" && isOpen){
      if (initialRenderRef.current) {
        initialRenderRef.current = false;
        return;
      }
   
      if (editorRef.current && typeof editorRef.current.setContents === "function") {
        const currentContent = editorRef.current.getContents(true);
        
        if (currentContent !== value) {
          editorRef.current.setContents(value);
          setLastContent(value);
          syncEditorPlaceholder(editorRef.current.core, value);
        }
      }
    }
  }, [isOpen, value, from]);

// Add keyboard event handlers and Safari detection
useEffect(() => {
  let handleFocus, handleBlur;

  // Only define the focus and blur handlers if the browser is Safari on iOS
  if (isSafariIOS()) {
    handleFocus = () => {
      document.body.classList.add('keyboard-open');
    };

    handleBlur = () => {
      document.body.classList.remove('keyboard-open');
    };
  }

  // Attach the focus and blur event handlers to SunEditor if Safari is detected
  if (editorRef.current && isSafariIOS()) {
    editorRef.current.onFocus = handleFocus;
    editorRef.current.onBlur = handleBlur;
  }

  // Cleanup on component unmount
  return () => {
    if (editorRef.current) {
      editorRef.current.onFocus = null;
      editorRef.current.onBlur = null;
    }
  };
}, []); // Empty dependency array means this effect runs only once when the component mounts

  const handleDrop = () => {
    if (isPasteRestrictedUser) {
      showPasteBlockedAlert();
      return false;
    }
  };

  useEffect(() => {
    try {
      if (!editorRef.current || typeof editorRef.current.setContents !== "function") return;

      const currentContent = editorRef.current.getContents(true);
      const isEmpty = currentContent === "<p><br></p>" || currentContent === "";

      if (disabled) {
        if (currentContent !== value) {
          editorRef.current.setContents(value || "");
          syncEditorPlaceholder(editorRef.current.core, value || "");
        }
      } else if (isEmpty && (value || (has_limited_option && !value))) {
        editorRef.current.setContents(value);
        syncEditorPlaceholder(editorRef.current.core, value);
      }
    } catch (error) {
      Sentry.captureException(`254 Error in useEffect: ${error}`);
    }
  }, [value, disabled, has_limited_option]);

  const preventCopyCut = useCallback((e, type = "Copying") => {
    try {
      if (user?.role === "owe_admin" || user?.role === "teacher") return;
      e.preventDefault();
      showAlert(`${type} is not allowed`, "error");
      return false;
    } catch (error) {
      Sentry.captureException(`256 Error in preventCopyCut: ${error}`);
    }
  }, [showAlert, user]);

  const insertHTML = (html) => {
    const _editor = editorRef.current;
    if (_editor) {
      _editor.insertHTML(html);
    }
  };

  const escapeHtml = (text = "") =>
    text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const handleEditorChange = useCallback(
    (nextValue) => {
      const cleanValue =
        typeof nextValue === "string" ? nextValue.replace(/<img[^>]*>/g, "") : nextValue;
      setLastContent(cleanValue);
      handleChange(cleanValue);
      if (isPasteRestrictedUser) {
        schedulePushStudentHistory(cleanValue);
      }
    },
    [handleChange, isPasteRestrictedUser, schedulePushStudentHistory]
  );

  const handleStudentKeyDown = useCallback(
    (event) => {
      if (!isPasteRestrictedUser) return;
      const isMod = event.ctrlKey || event.metaKey;
      if (!isMod) return;

      if (event.key === "z" || event.key === "Z") {
        event.preventDefault();
        if (event.shiftKey) {
          studentRedo();
        } else {
          studentUndo();
        }
      }

      if (event.key === "y" || event.key === "Y") {
        event.preventDefault();
        studentRedo();
      }
    },
    [isPasteRestrictedUser, studentRedo, studentUndo]
  );

  const handleEditorPaste = useCallback(
    (event, cleanData, maxCharCount) => {
      try {
        if (isPasteRestrictedUser) {
          showPasteBlockedAlert();
          return false;
        }

        const clipboardText = event?.clipboardData?.getData("text/plain") || "";
        if (clipboardText) {
          event.preventDefault();
          const plainTextHtml = escapeHtml(clipboardText).replace(/\r?\n/g, "<br>");
          insertHTML(plainTextHtml);
          return false;
        }
      } catch (error) {
        Sentry.captureException(`255 Error in onPaste: ${error}`);
      }
      return undefined;
    },
    [isPasteRestrictedUser, showPasteBlockedAlert]
  );

  useEffect(() => {
    if (!disableWordSuggestions) return;
    let attempt = 0;
    const maxAttempts = 20;
    const applySettings = () => {
      const editableElement = editorRef.current?.context?.element?.wysiwyg;
      if (!editableElement) return false;
      const targets = [
        editableElement,
        ...(editableElement.querySelectorAll?.('[contenteditable="true"]') || []),
      ];
      targets.forEach((node) => {
        node.setAttribute("spellcheck", "false");
        node.spellcheck = false;
        node.setAttribute("autocorrect", "off");
        node.setAttribute("autocorrect", "false");
        node.setAttribute("autocapitalize", "none");
        node.setAttribute("autocomplete", "off");
        node.setAttribute("aria-autocomplete", "none");
        node.setAttribute("x-webkit-speech", "off");
        node.setAttribute("x-webkit-voice", "off");
        node.setAttribute("data-gramm", "false");
        node.setAttribute("data-gramm_editor", "false");
        node.setAttribute("data-grammarly", "false");
        node.setAttribute("data-grammarly_editor", "false");
        node.setAttribute("data-enable-grammarly", "false");
      });
      return true;
    };
    const intervalId = setInterval(() => {
      attempt++;
      const applied = applySettings();
      if (applied || attempt >= maxAttempts) clearInterval(intervalId);
    }, 100);
    return () => clearInterval(intervalId);
  }, [disableWordSuggestions]);

  return (
    <Container tabIndex={0} ref={ref} className={`${isFocused ? "disable-key-navigation" : ""}`}>
      <SunEditor

        getSunEditorInstance={getSunEditorInstance}
        setAllPlugins={true}
        autoFocus={autoFocus}
        disable={disabled}
        disableToolbar={disabled}
        onDrop={handleDrop}
        onKeyDown={handleStudentKeyDown}
        placeholder={placeholder}
        // placeholder={(() => {
        //     const tempElement = document.createElement("div");
        //                   tempElement.innerHTML = value;
        //                   const textContent =
        //                     tempElement.textContent || tempElement.innerText;
        //                   if(textContent.length === 0){
        //                     return "Please type here...";
        //                   }
        //                   else {
        //                     return ''
        //                   }
        // })()}
        setOptions={{
          height: "100%",
          width: "100%",
          stickyToolbar: containerClassName !== "" && false,
          showPathLabel: false,
          buttonList: editorButtonList,
          shortcutsDisable: isPasteRestrictedUser ? ["undo", "redo"] : [],
          plugins: editorPlugins,
          callBackSave: (content, isChanged) => {

            if (isChanged) {
              handleEditorChange(content);
            }
          },
          frameAttrbutes: disableWordSuggestions
            ? {
              spellcheck: "false",
              autocorrect: "off",
              autocapitalize: "none",
              autocomplete: "off",
              "aria-autocomplete": "none",
              "x-webkit-speech": "off",
              "x-webkit-voice": "off",
              "data-gramm": "false",
              "data-gramm_editor": "false",
              "data-grammarly": "false",
              "data-grammarly_editor": "false",
              "data-enable-grammarly": "false",
            }
            : {},
          ...containerStyle
        }}
        setDefaultStyle={"text-align: left; border-bottom: 1px solid #393E46; background-color: #fff;"}
        onPaste={handleEditorPaste}
        onCopy={(e) => preventCopyCut(e, "Copying")}
        onCut={(e) => preventCopyCut(e, "Cutting")}
        onChange={handleEditorChange}
        defaultValue={value}
      />
    </Container>
  );
};


export default TextEditor;
